/* eslint-disable react/no-unstable-nested-components */
import ActivatePremiumDialog from "components/billing/ActivatePremiumDialog";
import classNames from "components/ui/classNames";
import { useAmplitude } from "lib/amplitude/Amplitude";
import useSelfie from "lib/hooks/useSelfie";
import Link from "next/link";
import type { ReactElement, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

interface UnlockPremiumContentCTAProps {
  origin: string;
  freeMessage?: string;
  trialMessage?: ReactElement;
}

export default function UnlockPremiumContentCTA({ origin, freeMessage, trialMessage }: UnlockPremiumContentCTAProps) {
  const amplitude = useAmplitude();
  const selfie = useSelfie();

  return (
    <div className="flex items-center space-x-4 rounded-lg bg-blue-50 px-4 text-center dark:bg-blue-600/20">
      <span className="relative inline-flex h-2 w-2 md:h-2 md:w-2">
        <span
          className={classNames(
            "animate-ping-slow absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-600/50 opacity-80",
          )}
        />
        <span className={classNames("relative inline-flex h-2 w-2 rounded-full bg-blue-600 md:h-2 md:w-2")} />
      </span>

      {(!selfie || !selfie.isPremium) && (
        <Link
          href="/premium"
          onClick={() => {
            amplitude.logEvent("Click on link to become Premium", {
              origin,
            });
          }}
        >
          <p
            className={classNames(
              "rounded-xl py-2 text-sm font-medium leading-6 tracking-wide text-blue-700 dark:text-blue-300 dark:hover:text-blue-100",
            )}
          >
            {freeMessage || (
              <FormattedMessage defaultMessage="Para ver a tabela completa, você precisa ser Premium." id="r1ee0a" />
            )}
          </p>
        </Link>
      )}

      {selfie && selfie.isTrialing && (
        <div
          className={classNames(
            "rounded-xl text-sm font-medium leading-6 tracking-wide text-blue-700 dark:text-blue-300 dark:hover:text-blue-100",
          )}
        >
          {trialMessage || <TrialCTAContentForTable />}
        </div>
      )}
    </div>
  );
}

interface ActivateSubscriptionButtonProps {
  isLoading: boolean;
  children: ReactNode[];
}
function ActivateSubscriptionButton({ isLoading, children }: ActivateSubscriptionButtonProps) {
  return (
    <button
      disabled={isLoading}
      type="button"
      className={classNames(
        "inline-flex text-sm font-medium tracking-wide text-teal-600 underline-offset-4 hover:underline hover:opacity-80",
      )}
    >
      {isLoading ? <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" /> : children}
    </button>
  );
}

function TrialCTAContentForTable() {
  return (
    <ActivatePremiumDialog>
      {({ isLoading }) => (
        <p className="font-medium">
          <FormattedMessage
            defaultMessage="Ative sua assinatura Premium para ver a tabela completa. <activateSubscriptionButton>Clique para ativar sua assinatura Premium</activateSubscriptionButton> e ter acesso completo!"
            id="mae6vm"
            values={{
              activateSubscriptionButton: (text) => (
                <ActivateSubscriptionButton isLoading={isLoading}>{text}</ActivateSubscriptionButton>
              ),
            }}
          />
        </p>
      )}
    </ActivatePremiumDialog>
  );
}

export function TrialCTAContentForChart() {
  return (
    <ActivatePremiumDialog>
      {({ isLoading }) => (
        <p className="font-medium">
          <FormattedMessage
            defaultMessage="Ative sua assinatura Premium para ver o gráfico completo. <activateSubscriptionButton>Clique para ativar sua assinatura Premium</activateSubscriptionButton> e ter acesso completo!"
            id="qaY76M"
            values={{
              activateSubscriptionButton: (text) => (
                <ActivateSubscriptionButton isLoading={isLoading}>{text}</ActivateSubscriptionButton>
              ),
            }}
          />
        </p>
      )}
    </ActivatePremiumDialog>
  );
}

export function TrialCTAContentForHighlights() {
  return (
    <ActivatePremiumDialog>
      {({ isLoading }) => (
        <p className="font-medium">
          <FormattedMessage
            defaultMessage="Ative sua assinatura Premium para ver os Destaques Trimestrais completos. <activateSubscriptionButton>Clique para ativar sua assinatura Premium</activateSubscriptionButton> e ter acesso completo!"
            id="KfKX1O"
            values={{
              activateSubscriptionButton: (text) => (
                <ActivateSubscriptionButton isLoading={isLoading}>{text}</ActivateSubscriptionButton>
              ),
            }}
          />
        </p>
      )}
    </ActivatePremiumDialog>
  );
}
